import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines } from "@/components/site/Motion";
import { journals, site } from "@/content/site";

const CoverStack = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const lift = useTransform(scrollY, [0, 600], [0, -80]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative h-[440px] sm:h-[560px] lg:h-[640px] [perspective:1400px]" data-testid="hero-cover-stack">
      <motion.div style={{ rotateX: rx, rotateY: ry, y: lift }} className="absolute inset-0 [transform-style:preserve-3d]">
        <motion.div initial={{ opacity: 0, x: 60, rotate: 6 }} animate={{ opacity: 1, x: 0, rotate: 5 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.7 }} className="absolute right-[2%] top-[4%] w-[52%] [transform:translateZ(-60px)]">
          <Link to={`/index.php/${journals[1].slug}`} aria-label={journals[1].title} data-testid="hero-cover-njfs">
            <img src={journals[1].cover} alt={`${journals[1].title} cover`} className="w-full aspect-[2/3] object-cover shadow-[0_30px_60px_-20px_rgba(26,26,26,0.35)]" />
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 80, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: -4 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.5 }} className="absolute left-[6%] bottom-[2%] w-[58%] [transform:translateZ(60px)]">
          <Link to={`/index.php/${journals[0].slug}`} aria-label={journals[0].title} data-testid="hero-cover-esgs">
            <img src={journals[0].cover} alt={`${journals[0].title} cover`} className="w-full aspect-[2/3] object-cover shadow-[0_40px_80px_-24px_rgba(15,37,55,0.5)]" />
          </Link>
          <span className="absolute -left-3 -top-3 bg-[var(--paper)] border border-[var(--line)] font-mono-x text-[11px] tracking-[0.18em] uppercase px-3 py-1.5">ESGS · Featured</span>
        </motion.div>
      </motion.div>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.4, type: "spring", stiffness: 140 }} className="absolute right-[4%] bottom-[10%] w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-[var(--gold)] text-[var(--ink)] grid place-items-center text-center z-10">
        <span className="font-display italic text-xl leading-tight">Open<br />access</span>
      </motion.div>
    </div>
  );
};

export const HomeHero = () => (
  <section className="relative grain overflow-hidden" data-testid="home-hero">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 lg:pt-20 pb-16 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 relative z-10">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="eyebrow mb-8 flex items-center gap-3">
          <span className="w-8 h-px bg-[var(--emerald)]" /> Welcome to {site.name}
        </motion.p>
        <MaskLines as="h1" lines={["Advancing", <>knowledge <em className="text-[var(--emerald)]">across</em></>, "disciplines."]} className="font-display text-[3.4rem] sm:text-7xl lg:text-[7.2rem] leading-[0.95] tracking-[-0.02em]" />
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75 }} className="mt-10 max-w-xl text-lg text-[var(--ink-muted)] leading-relaxed">
          {site.intro}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }} className="mt-10 flex flex-wrap gap-4">
          <a href="#journals" className="group inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full bg-[var(--emerald)] text-[var(--paper)] hover:bg-[var(--emerald-2)] transition-colors" data-testid="hero-explore-button">
            Explore our journals
            <span className="w-9 h-9 rounded-full bg-[var(--paper)] text-[var(--emerald)] grid place-items-center transition-transform group-hover:translate-y-0.5"><ArrowDown className="w-4 h-4" /></span>
          </a>
          <Link to="/index.php/ESGS/about/submissions" className="group inline-flex items-center gap-2 h-14 px-7 rounded-full border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors" data-testid="hero-submit-button">
            Make a submission <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <CoverStack />
      </div>
    </div>
  </section>
);
